import { tx as translateInterfaceText } from '@/utils/i18n';
import { useLanguage } from '@/hooks/useLanguage';
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Plus } from 'lucide-react';
import { Layout } from '@/components/layout/Layout';
import { PlaylistGrid } from '@/components/playlist/PlaylistGrid';
import { PlaylistCard } from '@/components/playlist/PlaylistCard';
import { PlaylistGridSkeleton } from '@/components/playlist/PlaylistCardSkeleton';
import { PlaylistEmptyState } from '@/components/playlist/PlaylistEmptyState';
import { usePlaylistStore } from '@/store/playlist/store';
import { Playlist, PlaylistTemplate } from '@/store/playlist/types';
import { nanoid } from 'nanoid';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { useToast } from '@/hooks/use-toast';
import { CopyCodeModal } from '@/components/shared/CopyCodeModal';

// Mock data para demonstração - 10 playlists com variação
const mockPlaylists: Playlist[] = [
  {
    id: '1',
    name: 'Curso de Marketing Digital',
    description: 'Aprenda as melhores estratégias de marketing digital',
    thumbnailUrl: 'https://images.unsplash.com/photo-1460925895917-afdab827c52f?w=400&h=225&fit=crop',
    access: 'public',
    videos: [
      { id: 'v1', videoId: 'vid1', order: 0 },
      { id: 'v2', videoId: 'vid2', order: 1 },
      { id: 'v3', videoId: 'vid3', order: 2 },
      { id: 'v4', videoId: 'vid4', order: 3 },
      { id: 'v5', videoId: 'vid5', order: 4 },
      { id: 'v6', videoId: 'vid6', order: 5 },
    ],
    theme: 'blue',
    template: 'netflix',
    showProgress: true,
    enableFunnel: false,
    createdAt: '2024-11-15T10:30:00Z',
    updatedAt: new Date().toISOString(),
    totalDuration: 840,
    totalViews: 1234,
    totalPlays: 856,
    publicHash: 'abc123',
  },
  {
    id: '2',
    name: 'Tutorial Completo de Vendas',
    description: 'Do básico ao avançado em estratégias de vendas',
    thumbnailUrl: 'https://images.unsplash.com/photo-1553028826-f4804a6dba3b?w=400&h=225&fit=crop',
    access: 'link',
    videos: [
      { id: 'v7', videoId: 'vid7', order: 0 },
      { id: 'v8', videoId: 'vid8', order: 1 },
      { id: 'v9', videoId: 'vid9', order: 2 },
      { id: 'v10', videoId: 'vid10', order: 3 },
    ],
    theme: 'blue',
    template: 'panda',
    showProgress: true,
    enableFunnel: true,
    createdAt: '2024-10-20T14:00:00Z',
    updatedAt: new Date().toISOString(),
    totalDuration: 1800,
    totalViews: 856,
    totalPlays: 623,
    publicHash: 'def456',
  },
  {
    id: '3',
    name: 'Série: Produtividade',
    description: 'Aumente sua produtividade com técnicas comprovadas',
    thumbnailUrl: '',
    access: 'private',
    videos: [
      { id: 'v11', videoId: 'vid11', order: 0 },
      { id: 'v12', videoId: 'vid12', order: 1 },
      { id: 'v13', videoId: 'vid13', order: 2 },
      { id: 'v14', videoId: 'vid14', order: 3 },
      { id: 'v15', videoId: 'vid15', order: 4 },
      { id: 'v16', videoId: 'vid16', order: 5 },
      { id: 'v17', videoId: 'vid17', order: 6 },
      { id: 'v18', videoId: 'vid18', order: 7 },
    ],
    theme: 'dark',
    template: 'netflix',
    showProgress: false,
    enableFunnel: false,
    createdAt: '2024-09-05T08:15:00Z',
    updatedAt: new Date().toISOString(),
    totalDuration: 2520,
    totalViews: 432,
    totalPlays: 298,
    publicHash: 'ghi789',
  },
  {
    id: '4',
    name: 'Masterclass Finanças',
    description: 'Domine suas finanças pessoais',
    thumbnailUrl: 'https://images.unsplash.com/photo-1554224155-6726b3ff858f?w=400&h=225&fit=crop',
    access: 'public',
    videos: [
      { id: 'v19', videoId: 'vid19', order: 0 },
      { id: 'v20', videoId: 'vid20', order: 1 },
      { id: 'v21', videoId: 'vid21', order: 2 },
      { id: 'v22', videoId: 'vid22', order: 3 },
      { id: 'v23', videoId: 'vid23', order: 4 },
    ],
    theme: 'blue',
    template: 'clean',
    showProgress: true,
    enableFunnel: false,
    createdAt: '2024-12-01T16:45:00Z',
    updatedAt: new Date().toISOString(),
    totalDuration: 3600,
    totalViews: 2145,
    totalPlays: 1876,
    publicHash: 'jkl012',
  },
  {
    id: '5',
    name: 'Treinamento de Liderança',
    description: 'Desenvolva habilidades de liderança',
    thumbnailUrl: 'https://images.unsplash.com/photo-1552664730-d307ca884978?w=400&h=225&fit=crop',
    access: 'link',
    videos: [
      { id: 'v24', videoId: 'vid24', order: 0 },
      { id: 'v25', videoId: 'vid25', order: 1 },
      { id: 'v26', videoId: 'vid26', order: 2 },
    ],
    theme: 'blue',
    template: 'panda',
    showProgress: true,
    enableFunnel: false,
    createdAt: '2024-08-12T11:30:00Z',
    updatedAt: new Date().toISOString(),
    totalDuration: 1200,
    totalViews: 678,
    totalPlays: 512,
    publicHash: 'mno345',
  },
  {
    id: '6',
    name: 'Curso de Programação',
    description: 'Aprenda a programar do zero',
    thumbnailUrl: '',
    access: 'private',
    videos: [
      { id: 'v27', videoId: 'vid27', order: 0 },
      { id: 'v28', videoId: 'vid28', order: 1 },
      { id: 'v29', videoId: 'vid29', order: 2 },
      { id: 'v30', videoId: 'vid30', order: 3 },
      { id: 'v31', videoId: 'vid31', order: 4 },
      { id: 'v32', videoId: 'vid32', order: 5 },
      { id: 'v33', videoId: 'vid33', order: 6 },
      { id: 'v34', videoId: 'vid34', order: 7 },
      { id: 'v35', videoId: 'vid35', order: 8 },
      { id: 'v36', videoId: 'vid36', order: 9 },
      { id: 'v37', videoId: 'vid37', order: 10 },
      { id: 'v38', videoId: 'vid38', order: 11 },
    ],
    theme: 'dark',
    template: 'netflix',
    showProgress: true,
    enableFunnel: false,
    createdAt: '2024-07-28T09:00:00Z',
    updatedAt: new Date().toISOString(),
    totalDuration: 7200,
    totalViews: 3421,
    totalPlays: 2834,
    publicHash: 'pqr678',
  },
  {
    id: '7',
    name: 'Workshop de Design',
    description: 'Fundamentos do design gráfico',
    thumbnailUrl: 'https://images.unsplash.com/photo-1561070791-2526d30994b5?w=400&h=225&fit=crop',
    access: 'public',
    videos: [
      { id: 'v39', videoId: 'vid39', order: 0 },
      { id: 'v40', videoId: 'vid40', order: 1 },
      { id: 'v41', videoId: 'vid41', order: 2 },
      { id: 'v42', videoId: 'vid42', order: 3 },
    ],
    theme: 'blue',
    template: 'clean',
    showProgress: true,
    enableFunnel: false,
    createdAt: '2024-11-02T13:20:00Z',
    updatedAt: new Date().toISOString(),
    totalDuration: 2400,
    totalViews: 987,
    totalPlays: 745,
    publicHash: 'stu901',
  },
  {
    id: '8',
    name: 'Inglês para Negócios',
    description: 'Domine o inglês corporativo',
    thumbnailUrl: 'https://images.unsplash.com/photo-1434030216411-0b793f4b4173?w=400&h=225&fit=crop',
    access: 'link',
    videos: [
      { id: 'v43', videoId: 'vid43', order: 0 },
      { id: 'v44', videoId: 'vid44', order: 1 },
      { id: 'v45', videoId: 'vid45', order: 2 },
      { id: 'v46', videoId: 'vid46', order: 3 },
      { id: 'v47', videoId: 'vid47', order: 4 },
      { id: 'v48', videoId: 'vid48', order: 5 },
      { id: 'v49', videoId: 'vid49', order: 6 },
    ],
    theme: 'blue',
    template: 'panda',
    showProgress: true,
    enableFunnel: true,
    createdAt: '2024-06-18T17:10:00Z',
    updatedAt: new Date().toISOString(),
    totalDuration: 4200,
    totalViews: 1567,
    totalPlays: 1243,
    publicHash: 'vwx234',
  },
  {
    id: '9',
    name: 'Saúde e Bem-estar',
    description: 'Cuide da sua saúde física e mental',
    thumbnailUrl: '',
    access: 'public',
    videos: [
      { id: 'v50', videoId: 'vid50', order: 0 },
      { id: 'v51', videoId: 'vid51', order: 1 },
      { id: 'v52', videoId: 'vid52', order: 2 },
      { id: 'v53', videoId: 'vid53', order: 3 },
      { id: 'v54', videoId: 'vid54', order: 4 },
      { id: 'v55', videoId: 'vid55', order: 5 },
    ],
    theme: 'blue',
    template: 'clean',
    showProgress: true,
    enableFunnel: false,
    createdAt: '2024-10-05T10:00:00Z',
    updatedAt: new Date().toISOString(),
    totalDuration: 1800,
    totalViews: 2234,
    totalPlays: 1987,
    publicHash: 'yza567',
  },
  {
    id: '10',
    name: 'Empreendedorismo Digital',
    description: 'Crie seu negócio online do zero',
    thumbnailUrl: 'https://images.unsplash.com/photo-1559136555-9303baea8ebd?w=400&h=225&fit=crop',
    access: 'private',
    videos: [
      { id: 'v56', videoId: 'vid56', order: 0 },
      { id: 'v57', videoId: 'vid57', order: 1 },
      { id: 'v58', videoId: 'vid58', order: 2 },
      { id: 'v59', videoId: 'vid59', order: 3 },
      { id: 'v60', videoId: 'vid60', order: 4 },
      { id: 'v61', videoId: 'vid61', order: 5 },
      { id: 'v62', videoId: 'vid62', order: 6 },
      { id: 'v63', videoId: 'vid63', order: 7 },
      { id: 'v64', videoId: 'vid64', order: 8 },
      { id: 'v65', videoId: 'vid65', order: 9 },
    ],
    theme: 'dark',
    template: 'netflix',
    showProgress: true,
    enableFunnel: true,
    createdAt: '2024-11-28T20:30:00Z',
    updatedAt: new Date().toISOString(),
    totalDuration: 5400,
    totalViews: 4567,
    totalPlays: 3891,
    publicHash: 'bcd890',
  },
];

export default function PlaylistList() {
  useLanguage();
  const navigate = useNavigate();
  const { toast } = useToast();
  const { playlists, setPlaylists, addPlaylist, deletePlaylist } = usePlaylistStore();
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [playlistToDelete, setPlaylistToDelete] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [showEmbedModal, setShowEmbedModal] = useState(false);
  const [playlistToEmbed, setPlaylistToEmbed] = useState<Playlist | null>(null);

  // Simular carregamento com skeleton
  useEffect(() => {
    const timer = setTimeout(() => {
      if (playlists.length === 0) {
        setPlaylists(mockPlaylists);
      }
      setIsLoading(false);
    }, 800);

    return () => clearTimeout(timer);
  }, []);

  const handleCreatePlaylist = () => {
    const newPlaylist: Playlist = {
      id: nanoid(),
      name: '',
      description: '',
      thumbnailUrl: '',
      access: 'private',
      videos: [],
      theme: 'blue',
      template: 'clean',
      showProgress: true,
      enableFunnel: false,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      totalDuration: 0,
      totalViews: 0,
      totalPlays: 0,
      publicHash: nanoid(10)
    };

    addPlaylist(newPlaylist);
    navigate(`/playlists/${newPlaylist.id}/edit`);
  };

  const handleEdit = (id: string) => {
    navigate(`/playlists/${id}/edit`);
  };

  const handleShare = (playlist: Playlist) => {
    const shareUrl = `${window.location.origin}/playlist/p/${playlist.publicHash}`;
    navigator.clipboard.writeText(shareUrl);
    toast({
      title: translateInterfaceText("Link copiado!"),
      description: translateInterfaceText("O link da playlist foi copiado para a área de transferência."),
    });
  };

  const handleCopyCode = (playlist: Playlist) => {
    setPlaylistToEmbed(playlist);
    setShowEmbedModal(true);
  };

  const handleDeleteClick = (id: string) => {
    setPlaylistToDelete(id);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = () => {
    if (playlistToDelete) {
      deletePlaylist(playlistToDelete);
      toast({
        title: translateInterfaceText("Playlist excluída"),
        description: translateInterfaceText("A playlist foi removida com sucesso."),
      });
      setPlaylistToDelete(null);
      setDeleteDialogOpen(false);
    }
  };

  return (
    <Layout>
      <div className="min-h-screen bg-[#F8FAFC]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-6 sm:py-8">
          {/* Header - Sempre visível */}
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8">
            <div>
              <h1 className="text-[24px] md:text-[24px] text-[20px] font-bold text-[#0F172A]">
                {translateInterfaceText("Minhas Playlists")}</h1>
              <p className="text-[#64748B] text-[14px] md:text-[15px] mt-1">
                {translateInterfaceText("Organize seus vídeos em sequência e compartilhe com seu público")}</p>
            </div>
            <Button 
              onClick={handleCreatePlaylist}
              className="bg-[#2563EB] hover:bg-[#1D4ED8] text-white h-[44px] px-6 rounded-[10px] shadow-sm"
            >
              <Plus className="h-4 w-4 mr-2" />
              {translateInterfaceText("Criar Playlist")}</Button>
          </div>

          {/* Content */}
          {isLoading ? (
            <PlaylistGridSkeleton count={8} />
          ) : playlists.length === 0 ? (
            <PlaylistEmptyState onCreatePlaylist={handleCreatePlaylist} />
          ) : (
            <PlaylistGrid>
              {playlists.map((playlist) => (
                <PlaylistCard
                  key={playlist.id}
                  playlist={playlist}
                  onEdit={() => handleEdit(playlist.id)}
                  onShare={() => handleShare(playlist)}
                  onCopyCode={() => handleCopyCode(playlist)}
                  onDelete={() => handleDeleteClick(playlist.id)}
                />
              ))}
            </PlaylistGrid>
          )}

          {/* Alert Dialog para confirmação de exclusão */}
          <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
            <AlertDialogContent className="max-w-[420px] rounded-[14px]">
              <AlertDialogHeader>
                <AlertDialogTitle>{translateInterfaceText("Excluir playlist?")}</AlertDialogTitle>
                <AlertDialogDescription>
                  {translateInterfaceText("Esta ação não pode ser desfeita. A playlist será removida permanentemente.")}</AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>{translateInterfaceText("Cancelar")}</AlertDialogCancel>
                <AlertDialogAction
                  onClick={handleDeleteConfirm}
                  className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                >
                  {translateInterfaceText("Excluir")}</AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>

          {/* Modal de Embed Padronizado */}
          {playlistToEmbed && (
            <CopyCodeModal
              isOpen={showEmbedModal}
              onClose={() => {
                setShowEmbedModal(false);
                setPlaylistToEmbed(null);
              }}
              entityType="playlist"
              entityId={playlistToEmbed.id}
              entityName={playlistToEmbed.name || 'Playlist'}
              iframeCode={`<iframe src="https://player.hostvsl.com/playlist/${playlistToEmbed.id}" width="100%" height="500" frameborder="0" allowfullscreen></iframe>`}
              jsCode={`<div id="hostvsl-playlist-${playlistToEmbed.id}"></div>
<script src="https://player.hostvsl.com/embed.js"></script>
<script>
  HostVSL.init({
    container: "#hostvsl-playlist-${playlistToEmbed.id}",
    playlist: "${playlistToEmbed.id}",
  });
</script>`}
            />
          )}
        </div>
      </div>
    </Layout>
  );
}
